import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, X } from 'lucide-react';

export type NotificationType = 'success' | 'error' | 'info';

interface NotificationProps {
    type: NotificationType;
    message: string;
    duration?: number;
    onClose?: () => void;
}

const Notification: React.FC<NotificationProps> = ({
    type,
    message,
    duration = 3000,
    onClose
}) => {
    const [isVisible, setIsVisible] = useState(true);

    useEffect(() => {
        if (duration) {
            const timer = setTimeout(() => {
                setIsVisible(false);
                if (onClose) onClose();
            }, duration);

            return () => clearTimeout(timer);
        }
    }, [duration, onClose]);

    const handleClose = () => {
        setIsVisible(false);
        if (onClose) onClose();
    };

    if (!isVisible) return null;

    const getIcon = () => {
        switch (type) {
            case 'success':
                return <CheckCircle className="h-5 w-5 text-green-500" />;
            case 'error':
                return <XCircle className="h-5 w-5 text-red-500" />;
            case 'info':
                return <AlertCircle className="h-5 w-5 text-blue-500" />;
            default:
                return null;
        }
    };

    const getBgColor = () => {
        switch (type) {
            case 'success':
                return 'bg-green-50';
            case 'error':
                return 'bg-red-50';
            case 'info':
                return 'bg-blue-50';
            default:
                return 'bg-gray-50';
        }
    };

    const getBorderColor = () => {
        switch (type) {
            case 'success':
                return 'border-green-400';
            case 'error':
                return 'border-red-400';
            case 'info':
                return 'border-blue-400';
            default:
                return 'border-gray-300';
        }
    };

    const getTextColor = () => {
        switch (type) {
            case 'success':
                return 'text-green-800';
            case 'error':
                return 'text-red-800';
            case 'info':
                return 'text-blue-800';
            default:
                return 'text-gray-800';
        }
    };

    return (
        <div className={`fixed top-4 right-4 z-50 max-w-sm p-4 rounded-lg shadow-md border ${getBgColor()} ${getBorderColor()}`}>
            <div className="flex items-start">
                <div className="flex-shrink-0 mt-0.5">
                    {getIcon()}
                </div>
                <div className="ml-3 flex-1">
                    <p className={`text-sm font-medium ${getTextColor()}`}>{message}</p>
                </div>
                <button
                    onClick={handleClose}
                    className="ml-4 flex-shrink-0 text-gray-400 hover:text-gray-600"
                >
                    <X className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
};

export default Notification;